import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { ArrowRight, Globe2, Trophy, ArrowLeft } from "lucide-react";
import { fetchLeaderboard } from "../lib/api";
import { BrandMark, BrandWordmark } from "./LandingPage";
import { fmtPct, fmtRatio, fmtDays } from "../lib/format";

const ROW_DEFS = [
  { key: "gross_margin", label: "Gross margin", fmt: (v) => fmtPct(v) },
  { key: "ebitda_margin", label: "EBITDA margin", fmt: (v) => fmtPct(v) },
  { key: "net_margin", label: "Net margin", fmt: (v) => fmtPct(v) },
  { key: "current_ratio", label: "Current ratio", fmt: (v) => fmtRatio(v) },
  { key: "dscr", label: "DSCR", fmt: (v) => fmtRatio(v) },
  { key: "debt_to_ebitda", label: "Debt / EBITDA", fmt: (v) => fmtRatio(v) },
  { key: "cash_conversion_cycle", label: "Cash conv. cycle", fmt: (v) => fmtDays(v) },
];

const REGION_ALIASES = {
  US: "United States",
  CA: "Canada",
  UK: "United Kingdom",
  EU: "Eurozone",
  AU: "Australia / NZ",
  IN: "India",
  SG: "Singapore",
  AE: "UAE / GCC",
  ZA: "South Africa",
  NG: "Nigeria",
  KE: "Kenya / E. Africa",
  BR: "Brazil",
  MX: "LATAM",
  JP: "Japan",
  GLOBAL: "Global",
};

export default function LeaderboardPage() {
  const [data, setData] = useState(null);
  const [filterRegion, setFilterRegion] = useState("ALL");
  const [filterIndustry, setFilterIndustry] = useState("ALL");

  useEffect(() => {
    document.title = "CFO Grade Leaderboard — Anonymous community benchmarks";
    fetchLeaderboard(5).then(setData).catch(() => setData({ segments: [] }));
  }, []);

  const segments = data?.segments || [];

  const allRegions = useMemo(() => {
    const set = new Set(segments.map((s) => s.region));
    return ["ALL", ...Array.from(set)];
  }, [segments]);

  const allIndustries = useMemo(() => {
    const set = new Map();
    segments.forEach((s) => set.set(s.industry_key, s.industry_label));
    return [["ALL", "All industries"], ...Array.from(set.entries())];
  }, [segments]);

  const filtered = useMemo(() => {
    return segments.filter(
      (s) =>
        (filterRegion === "ALL" || s.region === filterRegion) &&
        (filterIndustry === "ALL" || s.industry_key === filterIndustry)
    );
  }, [segments, filterRegion, filterIndustry]);

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain relative">
      <Header />
      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-10 sm:py-14">
        <Hero count={segments.length} totalContribs={segments.reduce((a, s) => a + s.n, 0)} />

        {segments.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="mt-12 flex flex-wrap items-center gap-3" data-testid="leaderboard-filters">
              <Filter
                label="Region"
                value={filterRegion}
                options={allRegions.map((r) => [r, r === "ALL" ? "All regions" : REGION_ALIASES[r] || r])}
                onChange={setFilterRegion}
              />
              <Filter
                label="Industry"
                value={filterIndustry}
                options={allIndustries}
                onChange={setFilterIndustry}
              />
              <span className="text-xs text-[#5C665D] ml-auto">
                {filtered.length} of {segments.length} segments
              </span>
            </div>

            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="leaderboard-grid">
              {filtered.map((s) => {
                const slug = `${s.industry_key.replace(/_/g, "-")}-${s.region.toLowerCase()}`;
                return (
                  <Link
                    key={`${s.industry_key}-${s.region}`}
                    to={`/leaderboard/${slug}`}
                    data-testid={`leaderboard-link-${slug}`}
                    className="block focus:outline-none focus:ring-2 focus:ring-[#1F382E] rounded-lg"
                  >
                    <SegmentCard segment={s} />
                  </Link>
                );
              })}
            </div>
          </>
        )}

        <CtaBlock />
      </main>
      <SiteFooter />
    </div>
  );
}

function Header() {
  return (
    <header className="max-w-7xl mx-auto px-6 sm:px-10 pt-8 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5">
        <BrandWordmark size="md" />
      </Link>
      <div className="flex items-center gap-3">
        <Link to="/">
          <Button variant="ghost" className="text-[#5C665D] hover:bg-[#D4C9B6]/40">
            <ArrowLeft size={14} strokeWidth={1.5} className="mr-2" />
            Home
          </Button>
        </Link>
        <Link to="/analyze">
          <Button
            variant="outline"
            className="border-[#1F382E] text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] transition-all duration-300"
            data-testid="leaderboard-run-analysis-btn"
          >
            Run my analysis
            <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
          </Button>
        </Link>
      </div>
    </header>
  );
}

function Hero({ count, totalContribs }) {
  return (
    <section className="pt-12 sm:pt-16 grid lg:grid-cols-12 gap-12 items-end fade-up">
      <div className="lg:col-span-8">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#5C665D] border border-[#E5E0D8] bg-white/70 rounded-full px-3 py-1.5">
          <Trophy size={11} strokeWidth={1.5} className="text-[#C26D5C]" />
          Public leaderboard
        </span>
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-[#1F382E] leading-[1.02] tracking-tight mt-6">
          Where do
          <span className="italic text-[#C26D5C]"> you </span>
          stand?
        </h1>
        <p className="text-base sm:text-lg text-[#5C665D] leading-relaxed mt-6 max-w-2xl">
          Real, anonymous medians from operators who chose to share their ratios. No marketing
          numbers, no LinkedIn humble-brags — just what businesses in your industry actually
          look like under the hood.
        </p>
      </div>
      <div className="lg:col-span-4">
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 grid grid-cols-2 gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">Segments live</p>
            <p className="font-serif-display text-4xl text-[#1F382E] tabular-nums leading-none mt-1">
              {count}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">Contributions</p>
            <p className="font-serif-display text-4xl text-[#1F382E] tabular-nums leading-none mt-1">
              {totalContribs}
            </p>
          </div>
          <div className="col-span-2 pt-3 border-t border-[#E5E0D8]">
            <p className="text-xs text-[#5C665D] leading-relaxed">
              Industry × region segments unlock at <strong>n = 5</strong> contributions.
              Add yours when you run an analysis.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <div className="mt-12 bg-white border border-[#E5E0D8] rounded-2xl p-12 text-center max-w-2xl mx-auto">
      <Globe2 size={28} strokeWidth={1.5} className="mx-auto text-[#C26D5C]" />
      <h3 className="font-serif-display text-2xl text-[#1F382E] mt-4">
        The board is filling up.
      </h3>
      <p className="text-[#5C665D] mt-3 leading-relaxed">
        No segment has crossed the n = 5 threshold yet. Run your own analysis and tick the
        <em> "share my ratios anonymously"</em> box to put your industry and region on the map.
      </p>
      <Link to="/analyze">
        <Button className="mt-6 bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md transition-all duration-300">
          Be the first
          <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
        </Button>
      </Link>
    </div>
  );
}

function Filter({ label, value, options, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border border-[#E5E0D8] rounded-md text-sm px-3 py-1.5 text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#1F382E]"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </div>
  );
}

function SegmentCard({ segment }) {
  const regionLabel = REGION_ALIASES[segment.region] || segment.region;
  return (
    <Card
      className="bg-white border-[#E5E0D8] shadow-none hover:-translate-y-0.5 hover:shadow-sm hover:border-[#C26D5C]/40 transition-all duration-300 h-full"
      data-testid={`leaderboard-card-${segment.industry_key}-${segment.region}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardDescription className="text-[10px] uppercase tracking-[0.22em] text-[#5C665D]">
              {regionLabel}
            </CardDescription>
            <CardTitle className="font-serif-display text-xl text-[#1F382E] leading-tight mt-1">
              {segment.industry_label}
            </CardTitle>
          </div>
          <span className="bg-[#2C5C43]/10 text-[#2C5C43] text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
            n = {segment.n}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {ROW_DEFS.map((r) => {
            const v = segment.medians?.[r.key];
            if (v === undefined || v === null) return null;
            return (
              <li
                key={r.key}
                className="flex items-center justify-between text-sm border-b border-[#F0EDE5] last:border-b-0 pb-1.5 last:pb-0"
              >
                <span className="text-[#5C665D]">{r.label}</span>
                <span className="tabular-nums text-[#1F382E]">{r.fmt(v)}</span>
              </li>
            );
          })}
        </ul>
        <div className="mt-4 pt-3 border-t border-[#F0EDE5] flex items-center justify-between text-xs">
          <span className="text-[#5C665D]">Industry deep-dive</span>
          <span className="text-[#C26D5C] font-medium inline-flex items-center gap-1">
            View segment <ArrowRight size={11} strokeWidth={2} />
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function CtaBlock() {
  return (
    <section className="mt-20">
      <div className="bg-[#1F382E] rounded-2xl p-10 sm:p-14 text-[#F7F5F0] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #C26D5C 0%, transparent 40%), radial-gradient(circle at 80% 80%, #D4C9B6 0%, transparent 40%)",
          }}
        />
        <div className="relative max-w-3xl">
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight">
            See where your business actually sits.
          </h2>
          <p className="text-[#D4C9B6] mt-4 leading-relaxed max-w-xl">
            Five minutes. No login. Get your CFO Grade, full peer comparison, and a CFO-style
            memo. Tick the share box and you'll appear here, anonymously, the moment your segment
            unlocks.
          </p>
          <Link to="/analyze">
            <Button
              data-testid="leaderboard-cta-bottom"
              className="mt-8 bg-[#C26D5C] hover:bg-[#a85a4b] text-white rounded-md h-12 px-7 transition-all duration-300"
            >
              Run my analysis <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-[#E5E0D8] py-10 max-w-7xl mx-auto px-6 sm:px-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-[#5C665D]">
      <div className="flex items-center gap-2">
        <BrandMark />
        <span>© {new Date().getFullYear()} CFO Grade. Educational insights for business owners — not financial advice.</span>
      </div>
      <div>
        <Link to="/disclaimer" className="hover:text-[#1F382E] underline underline-offset-2">Full terms</Link>
        <span className="mx-2 text-[#D4C9B6]">·</span>
        <Link to="/admin" className="hover:text-[#1F382E]">Admin</Link>
      </div>
    </footer>
  );
}
